import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "../lib/api";
import { formatEuro, formatDate } from "../lib/format";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Checkbox } from "./ui/checkbox";
import { Printer, Archive, Loader2, Users, Undo2, Folder, FolderOpen, ChevronRight } from "lucide-react";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "./ui/alert-dialog";
import { toast } from "sonner";
import { buildRiepilogoCompensiPdf } from "../lib/riepilogoCompensiPdf";
import { studioHeaderData } from "../lib/proformaPdf";
import { useAuth } from "../context/AuthContext";

const LBL = "text-[11px] uppercase tracking-wide text-slate-400 font-mono";

export const SEG_GEST_RESTORE = "pgp_seg_gest_restore";

export default function SegnalatoreGestioneDialog({ open, onOpenChange, data, onChanged, initial }) {
  const navigate = useNavigate();
  const [tab, setTab] = useState(initial?.tab || "da_fatturare");
  const [da, setDa] = useState(initial?.da || "");
  const [a, setA] = useState(initial?.a || "");
  const [soloRicevuti, setSoloRicevuti] = useState(initial?.soloRicevuti ?? true);
  const [selected, setSelected] = useState(() => new Set());
  const [archiving, setArchiving] = useState(false);
  const [fatturate, setFatturate] = useState([]);
  const [loadingFatt, setLoadingFatt] = useState(false);
  const [confirmArch, setConfirmArch] = useState(false);
  const [restoring, setRestoring] = useState(null);
  const [openLotti, setOpenLotti] = useState(() => new Set(initial?.lotti || []));
  const toggleLotto = (k) => setOpenLotti((s) => { const n = new Set(s); if (n.has(k)) n.delete(k); else n.add(k); return n; });
  const lotti = (() => {
    const m = new Map();
    for (const f of fatturate) {
      const key = f.lotto_id || (f.created_at || f.data_archiviazione || "").slice(0, 19);
      const l = m.get(key) || { key, data: f.data_archiviazione, created: f.created_at || "", items: [], tot: 0 };
      l.items.push(f); l.tot += Number(f.compenso || 0);
      m.set(key, l);
    }
    return [...m.values()].sort((a, b) => (b.created || b.data || "").localeCompare(a.created || a.data || ""));
  })();

  const segnalatore = data?.segnalatore || "";
  const allPratiche = useMemo(() => (data?.pratiche || []).filter((p) => !p.archiviato), [data]);

  const daFatturare = useMemo(() => allPratiche.filter((p) => {
    if (soloRicevuti && !p.onorari_ricevuti) return false;
    if (!da && !a) return true;
    const di = p.data_incasso || "";
    if (!di) return false;
    if (da && di < da) return false;
    if (a && di > a) return false;
    return true;
  }), [allPratiche, da, a, soloRicevuti]);

  useEffect(() => { setSelected(new Set()); }, [da, a, open, soloRicevuti]);
  useEffect(() => { if (open) setSoloRicevuti(initial?.soloRicevuti ?? true); }, [open, initial]);

  const apriPratica = (e, id) => {
    e.stopPropagation();
    if (!id) return;
    const path = window.location.pathname;
    sessionStorage.setItem(SEG_GEST_RESTORE, JSON.stringify({ key: data?.segnalatore_key || "", path, tab, da, a, soloRicevuti, lotti: [...openLotti], ts: Date.now() }));
    navigate(`/app/pratiche?p=${id}&ret=${encodeURIComponent(path)}`);
  };

  const loadFatturate = () => {
    setLoadingFatt(true);
    api.get(`/studio/riepilogo-segnalatori/fatturate`, { params: { segnalatore } })
      .then(({ data }) => setFatturate(data || []))
      .catch((e) => toast.error(formatApiError(e.response?.data?.detail)))
      .finally(() => setLoadingFatt(false));
  };
  useEffect(() => {
    if (open && tab === "fatturate") loadFatturate();
    /* eslint-disable-next-line */
  }, [open, tab, segnalatore]);

  const ripristina = async (f) => {
    setRestoring(f.id);
    try {
      await api.delete(`/studio/riepilogo-segnalatori/fatturate/${f.id}`);
      toast.success(`Pratica ${f.numero || ""} riportata in "Da fatturare"`);
      setFatturate((list) => list.filter((x) => x.id !== f.id));
      onChanged && onChanged();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setRestoring(null);
    }
  };

  const toggle = (id) => setSelected((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const allSelected = daFatturare.length > 0 && daFatturare.every((p) => selected.has(p.id));
  const toggleAll = () => setSelected(() => (allSelected ? new Set() : new Set(daFatturare.map((p) => p.id))));

  const selectedRows = daFatturare.filter((p) => selected.has(p.id));
  const sum = (k) => daFatturare.reduce((s, p) => s + Number(p[k] || 0), 0);
  const totOnorari = sum("onorari");
  const totSpese = sum("spese");
  const totCompenso = sum("provvigione");
  const sumSel = (k) => selectedRows.reduce((s, p) => s + Number(p[k] || 0), 0);

  const periodoLabel = (da || a) ? `Periodo incasso onorari: ${da ? formatDate(da) : "\u2026"} \u2014 ${a ? formatDate(a) : "\u2026"}` : "";

  const { user } = useAuth();
  const printKey = `pgp_seg_print_cols_${user?.id || "anon"}`;
  const [printOpts, setPrintOpts] = useState(null);
  const stampaPdf = () => {
    if (selectedRows.length === 0) { toast.error("Seleziona almeno una pratica"); return; }
    let saved = { onorari: true, spese: true, intestazione: true };
    try { saved = { ...saved, ...JSON.parse(localStorage.getItem(printKey) || "{}") }; } catch { /* ignore */ }
    setPrintOpts(saved);
  };
  const loadHeader = async () => {
    const { data } = await api.get("/studio/me");
    const h = studioHeaderData(data);
    let logo = "";
    if (h.logo) {
      try {
        const blob = await fetch(h.logo).then((r) => r.blob());
        logo = await new Promise((res) => { const fr = new FileReader(); fr.onload = () => res(fr.result); fr.onerror = () => res(""); fr.readAsDataURL(blob); });
      } catch { logo = ""; }
    }
    return { nome: h.nome, logo, righe: [[h.indirizzo, h.citta].filter(Boolean).join(" — "), [h.telefono && `Tel. ${h.telefono}`, h.email].filter(Boolean).join("  ·  "), h.partita_iva && `P.IVA ${h.partita_iva}`] };
  };
  const doStampa = async () => {
    const o = printOpts;
    localStorage.setItem(printKey, JSON.stringify(o));
    setPrintOpts(null);
    let header = null;
    if (o.intestazione) { try { header = await loadHeader(); } catch { toast.error("Intestazione studio non disponibile"); } }
    buildRiepilogoCompensiPdf({
      segnalatore,
      periodo: periodoLabel,
      showOnorari: o.onorari, showSpese: o.spese, header,
      pratiche: selectedRows.map((p) => ({ numero: p.numero, cliente: p.cliente, stato: p.stato, onorari: p.onorari, spese: p.spese, compenso: p.provvigione })),
    });
  };

  const askArchivia = () => {
    if (selectedRows.length === 0) { toast.error("Seleziona almeno una pratica"); return; }
    setConfirmArch(true);
  };

  const archivia = async () => {
    setConfirmArch(false);
    setArchiving(true);
    try {
      await api.post(`/studio/riepilogo-segnalatori/archivia`, {
        segnalatore,
        segnalatore_key: data?.segnalatore_key || "",
        pratiche: selectedRows.map((p) => ({ id: p.id, numero: p.numero, cliente: p.cliente, stato: p.stato, onorari: p.onorari, spese: p.spese, compenso: p.provvigione })),
      });
      toast.success(`${selectedRows.length} pratiche archiviate nei Fatturate`);
      setSelected(new Set());
      onChanged && onChanged();
      onOpenChange(false);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setArchiving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#121824] border-slate-700 text-slate-100 max-w-4xl" data-testid="segnalatore-gestione-dialog">
        <DialogHeader>
          <DialogTitle className="font-head flex items-center gap-2"><Users className="h-5 w-5 text-amber-400" /> Gestione segnalatore · {segnalatore}</DialogTitle>
        </DialogHeader>

        <div className="flex gap-1 border-b border-slate-800">
          {[["da_fatturare", "Da fatturare"], ["fatturate", "Fatturate"]].map(([v, l]) => (
            <button key={v} type="button" onClick={() => setTab(v)} data-testid={`seg-tab-${v}`}
              className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${tab === v ? "border-amber-500 text-amber-400" : "border-transparent text-slate-400 hover:text-slate-200"}`}>{l}</button>
          ))}
        </div>

        {tab === "da_fatturare" ? (
          <div className="space-y-3 py-1">
            <div className="flex flex-wrap items-end gap-3">
              <div>
                <label className={LBL}>Da (incasso onorari)</label>
                <Input type="date" value={da} onChange={(e) => setDa(e.target.value)} data-testid="seg-filtro-da" className="mt-1 h-9 bg-slate-900/60 border-slate-700" />
              </div>
              <div>
                <label className={LBL}>A</label>
                <Input type="date" value={a} onChange={(e) => setA(e.target.value)} data-testid="seg-filtro-a" className="mt-1 h-9 bg-slate-900/60 border-slate-700" />
              </div>
              {(da || a) && (
                <Button type="button" variant="outline" onClick={() => { setDa(""); setA(""); }} data-testid="seg-filtro-reset" className="h-9 border-slate-700 text-slate-200 hover:bg-slate-800">Azzera</Button>
              )}
              <label className="h-9 flex items-center gap-2 text-sm text-slate-200 cursor-pointer select-none" data-testid="seg-filtro-ricevuti-label">
                <Checkbox checked={soloRicevuti} onCheckedChange={(v) => setSoloRicevuti(!!v)} data-testid="seg-filtro-ricevuti" />
                Solo con onorari ricevuti
              </label>
              <div className="ml-auto flex gap-2">
                <Button type="button" variant="outline" onClick={stampaPdf} data-testid="seg-stampa-pdf" className="h-9 border-slate-700 text-slate-200 hover:bg-slate-800"><Printer className="h-4 w-4 mr-1.5" /> Stampa riepilogo</Button>
                <Button type="button" onClick={askArchivia} disabled={archiving} data-testid="seg-archivia" className="h-9 bg-amber-500 hover:bg-amber-600 text-[#0a0d14] font-semibold">
                  {archiving ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Archive className="h-4 w-4 mr-1.5" />} Archivia selezionate
                </Button>
              </div>
            </div>

            {data && !data.compenso_configurato && (
              <div className="rounded-md border border-red-500/40 bg-red-500/10 px-3 py-2 text-xs text-red-300" data-testid="seg-compenso-non-configurato">
                Compenso non configurato: imposta in Anagrafiche → Segnalatori il compenso a percentuale e la base di calcolo (es. 20% sugli onorari), altrimenti il compenso resta 0,00 €.
              </div>
            )}
            <div className="rounded-md border border-slate-700 overflow-hidden max-h-[46vh] overflow-y-auto">
              <table className="w-full text-sm" data-testid="seg-da-fatturare-table">
                <thead>
                  <tr className="text-left text-slate-500 text-[10px] uppercase tracking-wider font-mono bg-slate-900/60 sticky top-0">
                    <th className="px-3 py-2 w-8"><Checkbox checked={allSelected} onCheckedChange={toggleAll} data-testid="seg-select-all" /></th>
                    <th className="px-3 py-2">N° Pratica</th>
                    <th className="px-3 py-2">Ragione sociale</th>
                    <th className="px-3 py-2">Stato</th>
                    <th className="px-3 py-2 text-right">Onorari</th>
                    <th className="px-3 py-2 text-right">Spese</th>
                    <th className="px-3 py-2 text-right">Compenso</th>
                  </tr>
                </thead>
                <tbody>
                  {daFatturare.length === 0 ? (
                    <tr><td colSpan={7} className="px-3 py-6 text-center text-slate-500 text-xs" data-testid="seg-da-fatturare-empty">Nessuna pratica da fatturare{soloRicevuti ? " con onorari ricevuti" : ""} per il periodo selezionato.</td></tr>
                  ) : daFatturare.map((p) => (
                    <tr key={p.id} data-testid={`seg-row-${p.id}`} className="border-t border-slate-800 hover:bg-slate-800/40 cursor-pointer" onClick={() => toggle(p.id)}>
                      <td className="px-3 py-2" onClick={(e) => e.stopPropagation()}><Checkbox checked={selected.has(p.id)} onCheckedChange={() => toggle(p.id)} data-testid={`seg-check-${p.id}`} /></td>
                      <td className="px-3 py-2 font-mono"><button type="button" onClick={(e) => apriPratica(e, p.id)} title="Apri pratica" data-testid={`seg-open-pratica-${p.id}`} className="text-amber-400 hover:text-amber-300 underline underline-offset-2 decoration-amber-500/40 hover:decoration-amber-300">{p.numero || "—"}</button></td>
                      <td className="px-3 py-2 text-slate-200">{p.cliente || "—"}</td>
                      <td className="px-3 py-2 text-slate-300 text-xs">{p.stato || "—"}</td>
                      <td className="px-3 py-2 text-right font-mono text-slate-300">{formatEuro(p.onorari)}</td>
                      <td className="px-3 py-2 text-right font-mono text-slate-300">{formatEuro(p.spese)}</td>
                      <td className="px-3 py-2 text-right font-mono text-teal-400 font-semibold">{formatEuro(p.provvigione)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="rounded-md border border-amber-500/40 bg-amber-500/10 px-4 py-3 flex flex-wrap items-center gap-x-8 gap-y-1 justify-between" data-testid="seg-summary">
              <span className="text-sm text-slate-200">Pratiche: <b data-testid="seg-sum-visible">{daFatturare.length}</b> <span className="text-slate-400">· selezionate <b data-testid="seg-sum-count">{selectedRows.length}</b></span></span>
              <span className="text-sm text-slate-300">Onorari: <b className="font-mono" data-testid="seg-sum-onorari">{formatEuro(totOnorari)}</b></span>
              <span className="text-sm text-slate-300">Spese: <b className="font-mono" data-testid="seg-sum-spese">{formatEuro(totSpese)}</b></span>
              <span className="text-sm text-slate-100">Totale compensi dovuti: <b className="font-mono text-teal-300" data-testid="seg-sum-compenso">{formatEuro(totCompenso)}</b></span>
            </div>
            <div className="mt-2 rounded-md border border-sky-500/40 bg-sky-500/10 px-4 py-3 flex flex-wrap items-center gap-x-8 gap-y-1 justify-between" data-testid="seg-summary-selected">
              <span className="text-sm text-slate-200">Pratiche selezionate: <b data-testid="seg-sel-count">{selectedRows.length}</b></span>
              <span className="text-sm text-slate-300">Onorari: <b className="font-mono" data-testid="seg-sel-onorari">{formatEuro(sumSel("onorari"))}</b></span>
              <span className="text-sm text-slate-300">Spese: <b className="font-mono" data-testid="seg-sel-spese">{formatEuro(sumSel("spese"))}</b></span>
              <span className="text-sm text-slate-100">Totale compensi: <b className="font-mono text-sky-300" data-testid="seg-sel-compenso">{formatEuro(sumSel("provvigione"))}</b></span>
            </div>
          </div>
        ) : (
          <div className="py-1">
            {loadingFatt ? (
              <div className="p-8 flex justify-center"><Loader2 className="h-5 w-5 animate-spin text-amber-500" /></div>
            ) : fatturate.length === 0 ? (
              <div className="p-8 text-center text-slate-400 text-sm" data-testid="seg-fatturate-empty">Nessuna pratica fatturata per questo segnalatore.</div>
            ) : (
              <div className="space-y-2 max-h-[56vh] overflow-y-auto pr-1" data-testid="seg-fatturate-folders">
                {lotti.map((l) => {
                  const isOpen = openLotti.has(l.key);
                  return (
                    <div key={l.key} className="rounded-md border border-slate-700 overflow-hidden" data-testid={`seg-fatt-folder-${l.key}`}>
                      <button type="button" onClick={() => toggleLotto(l.key)} data-testid={`seg-fatt-folder-toggle-${l.key}`}
                        className="w-full flex flex-wrap items-center gap-x-4 gap-y-1 px-3 py-2.5 bg-slate-900/60 hover:bg-slate-800/60 transition-colors text-left">
                        <ChevronRight className={`h-4 w-4 text-slate-400 transition-transform ${isOpen ? "rotate-90" : ""}`} />
                        {isOpen ? <FolderOpen className="h-5 w-5 text-amber-400" /> : <Folder className="h-5 w-5 text-amber-400" />}
                        <span className="text-sm font-semibold text-slate-100">Archiviazione del {formatDate(l.data)}</span>
                        <span className="text-xs text-slate-400">{l.items.length} {l.items.length === 1 ? "pratica" : "pratiche"}</span>
                        <span className="ml-auto text-sm">Totale compensi: <b className="font-mono text-teal-400" data-testid={`seg-fatt-folder-tot-${l.key}`}>{formatEuro(l.tot)}</b></span>
                      </button>
                      {isOpen && (
                <table className="w-full text-sm" data-testid={`seg-fatturate-table-${l.key}`}>
                  <thead>
                    <tr className="text-left text-slate-500 text-[10px] uppercase tracking-wider font-mono bg-slate-900/60 sticky top-0">
                      <th className="px-3 py-2">N° Pratica</th>
                      <th className="px-3 py-2">Ragione sociale</th>
                      <th className="px-3 py-2">Stato</th>
                      <th className="px-3 py-2 text-right">Onorari</th>
                      <th className="px-3 py-2 text-right">Spese</th>
                      <th className="px-3 py-2 text-right">Compenso</th>
                      <th className="px-3 py-2">Archiviata il</th>
                      <th className="px-3 py-2 w-10"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {l.items.map((f) => (
                      <tr key={f.id} data-testid={`seg-fatt-row-${f.pratica_id}`} className="border-t border-slate-800">
                        <td className="px-3 py-2 font-mono"><button type="button" onClick={(e) => apriPratica(e, f.pratica_id)} title="Apri pratica" data-testid={`seg-fatt-open-pratica-${f.pratica_id}`} className="text-amber-400 hover:text-amber-300 underline underline-offset-2 decoration-amber-500/40 hover:decoration-amber-300">{f.numero || "—"}</button></td>
                        <td className="px-3 py-2 text-slate-200">{f.cliente || "—"}</td>
                        <td className="px-3 py-2 text-slate-300 text-xs">{f.stato || "—"}</td>
                        <td className="px-3 py-2 text-right font-mono text-slate-300">{formatEuro(f.onorari)}</td>
                        <td className="px-3 py-2 text-right font-mono text-slate-300">{formatEuro(f.spese)}</td>
                        <td className="px-3 py-2 text-right font-mono text-teal-400 font-semibold">{formatEuro(f.compenso)}</td>
                        <td className="px-3 py-2 text-slate-300 text-xs">{formatDate(f.data_archiviazione)}</td>
                        <td className="px-2 py-1 text-right">
                          <Button type="button" size="sm" variant="ghost" disabled={restoring === f.id} onClick={() => ripristina(f)} title="Riporta in Da fatturare" data-testid={`seg-fatt-ripristina-${f.pratica_id}`} className="h-7 px-2 text-amber-400 hover:text-amber-300 hover:bg-slate-800">
                            {restoring === f.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Undo2 className="h-4 w-4" />}
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
        <AlertDialog open={!!printOpts} onOpenChange={(o) => { if (!o) setPrintOpts(null); }}>
          <AlertDialogContent className="bg-[#121824] border-slate-700 text-slate-100 max-w-sm" data-testid="seg-print-options">
            <AlertDialogHeader>
              <AlertDialogTitle>Opzioni di stampa</AlertDialogTitle>
              <AlertDialogDescription className="text-slate-400">Scegli le colonne da includere nel riepilogo PDF ({selectedRows.length} pratiche).</AlertDialogDescription>
            </AlertDialogHeader>
            {printOpts && (
              <div className="space-y-2 py-1">
                <label className="flex items-center gap-2 text-sm cursor-pointer"><Checkbox checked={printOpts.intestazione} onCheckedChange={(v) => setPrintOpts((o) => ({ ...o, intestazione: !!v }))} data-testid="seg-print-intestazione" /> Includi logo e intestazione studio</label>
                <label className="flex items-center gap-2 text-sm cursor-pointer"><Checkbox checked={printOpts.onorari} onCheckedChange={(v) => setPrintOpts((o) => ({ ...o, onorari: !!v }))} data-testid="seg-print-onorari" /> Includi colonna Onorari</label>
                <label className="flex items-center gap-2 text-sm cursor-pointer"><Checkbox checked={printOpts.spese} onCheckedChange={(v) => setPrintOpts((o) => ({ ...o, spese: !!v }))} data-testid="seg-print-spese" /> Includi colonna Spese</label>
              </div>
            )}
            <AlertDialogFooter>
              <AlertDialogCancel data-testid="seg-print-cancel" className="border-slate-700 bg-transparent text-slate-200 hover:bg-slate-800">Annulla</AlertDialogCancel>
              <AlertDialogAction onClick={doStampa} data-testid="seg-print-confirm" className="bg-amber-500 hover:bg-amber-600 text-[#0a0d14] font-semibold"><Printer className="h-4 w-4 mr-1.5" /> Stampa</AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
        <AlertDialog open={confirmArch} onOpenChange={setConfirmArch}>
          <AlertDialogContent className="bg-[#121824] border-slate-700 text-slate-100" data-testid="seg-archivia-confirm">
            <AlertDialogHeader>
              <AlertDialogTitle>Archiviare {selectedRows.length} {selectedRows.length === 1 ? "pratica" : "pratiche"}?</AlertDialogTitle>
              <AlertDialogDescription className="text-slate-400">
                Le pratiche selezionate (compenso totale {formatEuro(selectedRows.reduce((s, p) => s + Number(p.provvigione || 0), 0))}) verranno spostate nella tab "Fatturate". Potrai ripristinarle in qualsiasi momento.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel data-testid="seg-archivia-cancel" className="border-slate-700 bg-transparent text-slate-200 hover:bg-slate-800">Annulla</AlertDialogCancel>
              <AlertDialogAction onClick={archivia} data-testid="seg-archivia-confirm-btn" className="bg-amber-500 hover:bg-amber-600 text-[#0a0d14] font-semibold">Archivia</AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </DialogContent>
    </Dialog>
  );
}
